import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ReflectionService {
    constructor() {}

    public objectToString(obj: any): string {
        if (!obj) {
            return '';
        }
        if (typeof obj === 'string') {
            return obj;
        }
        if (typeof obj === 'object') {
            const strings: Array<string> = [];
            Object.keys(obj)
                .sort()
                .forEach((key) => {
                    strings.push(`${this.toTitleCase(key)}: ${this.objectToString(obj[key])}`);
                });
            return this.toEnglishSentence(strings);
        }
    }

    public getValueOrDefault<T>(value: T, defaultValue: T) {
        return this.isNotNullOrEmpty(value) ? value : defaultValue;
    }

    public stringDataMerge(obj: any, str: string) {
        if (!str || !obj) {
            return str;
        }
        try {
            let s = str;
            const matches = s.match(/({.*?})/g);
            if (matches && matches.length) {
                for (const i in matches) {
                    const strMatch = matches[i].match(/{(.*?)}/);
                    if (!strMatch || strMatch.length < 2) {
                        continue;
                    }

                    let value = this.propertyByString(obj, strMatch[1]);
                    value = this.isNotNullOrEmpty(value) ? value : '';
                    s = s.replace(matches[i], value);
                }
            }
            return s;
        } catch (e) {
            console.error(e);
        }
        return str;
    }

    public propertyByString(obj: any, property: string) {
        if (!obj || !property) {
            return;
        }
        property = property.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        property = property.replace(/^\./, ''); // strip a leading dot
        const a = property.split('.');
        for (let i = 0, n = a.length; i < n; ++i) {
            const k = a[i];
            if (k in obj) {
                obj = obj[k];
            } else {
                return;
            }
        }
        return obj;
    }

    public toEnglishSentence(strings: string[], concat: string = 'And', always: boolean = false) {
        if (strings && strings.length) {
            switch (strings.length) {
                case 1:
                    return strings[0];
                case 2:
                    return strings.join(` ${concat} `);
                default:
                    if (always) {
                        return strings.join(` ${concat} `);
                    }
                    return (
                        strings.slice(0, strings.length - 1).join(', ') + `, ${concat} ` + strings[strings.length - 1]
                    );
            }
        }
    }

    public toTitleCase(str: string) {
        if (!str) {
            return str;
        }
        //if the string is all caps leave it alone
        const regex = new RegExp(/^[A-Z_\s\-]+$/);
        if (regex.test(str)) {
            return str;
        }
        str = str.replace(/([A-Z])/g, ' $1');
        str = str.replace(/([^\W_]+[^\s-]*) */g, function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });

        // Certain minor words should be left lowercase unless
        // they are the first or last words in the string
        const lowers = [
            'A',
            'An',
            'The',
            'And',
            'But',
            'Or',
            'For',
            'Nor',
            'As',
            'At',
            'By',
            'For',
            'From',
            'In',
            'Into',
            'Near',
            'Of',
            'On',
            'Onto',
            'To',
            'With'
        ];
        for (let i = 0; i < lowers.length; i++) {
            str = str.replace(new RegExp('\\s' + lowers[i] + '\\s', 'g'), function (txt) {
                return txt.toLowerCase();
            });
        }

        // Certain words such as initialisms or acronyms should be left uppercase
        const uppers = ['Id'];
        for (let i = 0; i < uppers.length; i++) {
            str = str.replace(new RegExp('\\b' + uppers[i] + '\\b', 'g'), uppers[i].toUpperCase());
        }

        return str;
    }

    public isNullOrEmpty(value: any): boolean {
        return !this.isNotNullOrEmpty(value);
    }

    public isNotNullOrEmpty(value: any): boolean {
        return value !== null && value !== undefined && !/^\s*$/.test(value.toString());
    }

    public numberToBoolean(value: any): boolean {
        return !(value === 0 || value === '0');
    }

    public numberValueIsOneOf<T>(value: T, values: Array<T>, defaultValue: T = null) {
        if (this.isNotNullOrEmpty(value)) {
            return values.find((m) => m === value);
        }
        return defaultValue;
    }

    public updateProperties(copyInto: any, copyFrom: any): any {
        if (!copyInto || !copyFrom) {
            return copyInto;
        }
        const copyFromKeys = Object.keys(copyFrom);
        for (const key of copyFromKeys) {
            if (typeof copyFromKeys[key] === 'object') {
                copyInto[key] = this.updateProperties(copyInto[key], copyFrom[key]);
            } else {
                copyInto[key] = copyFrom[key];
            }
        }
        return copyInto;
    }

    public setPropertyByString(obj: any, property: string, value: any) {
        let schema = obj;
        const propertyList = property.split('.');
        const len = propertyList.length;
        for (let i = 0; i < len - 1; i++) {
            const element = propertyList[i];
            if (!schema[element]) {
                schema[element] = {};
            }
            schema = schema[element];
        }
        schema[propertyList[len - 1]] = value;
    }

    public toAlias(obj: string | string[]): string {
        if (!obj) {
            return '';
        }
        const array: Array<string> = this.collectArray(obj);
        const returnArray: Array<string> = [];
        array.forEach((value) => {
            if (!value) {
                return;
            }
            value = this.toTitleCase(value);
            value = value.trim().toString().replace(/\s+/gi, '_');
            returnArray.push(value.toLowerCase());
        });
        return returnArray.join('_');
    }

    public collectArray(obj: string | string[] | { [klass: string]: any }): Array<string> {
        let toReturn: Array<string> = [];
        if (obj) {
            if (typeof obj === 'string') {
                toReturn.push(obj as string);
            } else if (obj instanceof Array) {
                toReturn = obj as string[];
            } else {
                for (const k in obj) {
                    if (obj[k]) {
                        toReturn.push(k);
                    }
                }
            }
        }
        return toReturn;
    }

    public stringifyNumber(n: number): string {
        const special: Array<string> = [
            'zeroth',
            'first',
            'second',
            'third',
            'fourth',
            'fifth',
            'sixth',
            'seventh',
            'eighth',
            'ninth',
            'tenth',
            'eleventh',
            'twelfth',
            'thirteenth',
            'fourteenth',
            'fifteenth',
            'sixteenth',
            'seventeenth',
            'eighteenth',
            'nineteenth'
        ];
        const deca: Array<string> = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];
        if (n < 20) {
            return special[n];
        }
        if (n % 10 === 0) return deca[Math.floor(n / 10) - 2] + 'ieth';
        return deca[Math.floor(n / 10) - 2] + 'y-' + special[n % 10];
    }

    //meta is command on mac I think
    isCtrlOrMetaKey(event: any): boolean {
        if (event && (event?.ctrlKey || event?.metaKey)) {
            return true;
        }
        return false;
    }

    private getPlatform(): string {
        // 2022 way of detecting. Note : this userAgentData feature is available only in secure contexts (HTTPS)
        if (this.isNotNullOrEmpty(navigator?.userAgent)) {
            if (/android/.test(navigator?.userAgent?.toLowerCase())) {
                return 'android';
            }
            return navigator?.userAgent?.toLowerCase();
        }
        // Deprecated but still works for most of the browser
        if (this.isNotNullOrEmpty(navigator?.platform)) {
            return navigator?.platform?.toLowerCase();
        }
        return 'unknown';
    }

    private isMac(): boolean {
        return /mac/.test(this.getPlatform());
    }

    ctrlOrMetaKeyPlusClickString(suffix: string = '', prefix: string = ''): string {
        let body = 'Ctrl + Click';
        if (this.isMac()) {
            body = 'Command + Click';
        }
        let toReturn = body;
        if (this.isNotNullOrEmpty(prefix)) {
            toReturn = prefix + ' ' + toReturn;
        }
        if (this.isNotNullOrEmpty(suffix)) {
            toReturn = toReturn + ' ' + suffix;
        }
        return toReturn;
    }
}
